import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { POSPage } from './POSPage';
import {
  getCurrentProfile,
  getCurrentSession,
  signIn,
  signOut,
  subscribeToAuthChanges,
  type Profile,
} from '../services/auth/authService';
import { requestSync, setLocalDataRecoveryOpen } from '../services/sync';
import { LocalDataRecoveryDialog } from '../components/pos/LocalDataRecoveryDialog';

const cacheKey = 'babs.pos.verifiedProfile';

function readCachedProfile(userId: string): Profile | null {
  try {
    const value = JSON.parse(localStorage.getItem(cacheKey) ?? 'null') as Profile | null;
    return value?.id === userId && value.active && value.branch_id ? value : null;
  } catch {
    return null;
  }
}

function allowed(profile: Profile | null): profile is Profile {
  return Boolean(
    profile?.active && profile.branch_id && ['cashier', 'manager', 'owner'].includes(profile.role),
  );
}

export function POSAccessPage() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [recoveryOpen, setRecoveryOpen] = useState(false);

  const openRecovery = () => {
    setLocalDataRecoveryOpen(true);
    setRecoveryOpen(true);
  };

  const closeRecovery = () => {
    setLocalDataRecoveryOpen(false);
    setRecoveryOpen(false);
  };

  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        const session = await getCurrentSession();
        if (!session) {
          if (active) setProfile(null);
          return;
        }
        if (!navigator.onLine) {
          if (active) setProfile(readCachedProfile(session.user.id));
          return;
        }
        const current = await getCurrentProfile();
        if (!active) return;
        if (allowed(current)) {
          localStorage.setItem(cacheKey, JSON.stringify(current));
          setProfile(current);
          requestSync();
        } else {
          localStorage.removeItem(cacheKey);
          setProfile(null);
          setError('This account needs an active role and branch assignment.');
        }
      } catch (cause) {
        if (active) {
          setProfile(null);
          setError(cause instanceof Error ? cause.message : 'Unable to check cashier access.');
        }
      } finally {
        if (active) setChecking(false);
      }
    };
    void check();
    const unsubscribe = subscribeToAuthChanges(() => void check());
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      if (!navigator.onLine)
        throw new Error('Connect to the internet to sign in for the first time.');
      await signIn(email, password);
      const current = await getCurrentProfile();
      if (!allowed(current)) {
        await signOut();
        throw new Error('This account needs an active role and branch assignment.');
      }
      localStorage.setItem(cacheKey, JSON.stringify(current));
      setProfile(current);
      setPassword('');
      requestSync();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to sign in.');
    } finally {
      setBusy(false);
    }
  };

  if (checking)
    return <div className="p-8 text-center font-semibold">Checking cashier access…</div>;
  if (profile)
    return (
      <POSPage
        cashierId={profile.id}
        onSignOut={async () => {
          await signOut();
          localStorage.removeItem(cacheKey);
          setProfile(null);
        }}
      />
    );

  return (
    <div className="grid min-h-[calc(100vh-72px)] place-items-center p-5">
      <div className="w-full max-w-md space-y-4">
        <button
          type="button"
          onClick={openRecovery}
          className="min-h-14 w-full rounded-xl border-2 border-[#00549C] bg-white px-4 text-base font-black tracking-wide text-[#00549C] shadow-sm"
        >
          CHECK LOCAL DATA
        </button>
        <form
          onSubmit={submit}
          className="w-full rounded-2xl border border-slate-200 bg-white p-7 shadow-sm"
        >
          <h1 className="text-2xl font-black text-[#003B70]">Cashier sign in</h1>
          <p className="mt-2 text-sm text-slate-600">
            Sign in online once to download the active catalog. Your saved catalog and pending
            orders remain available if the connection drops.
          </p>
          {error && (
            <p role="alert" className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {error}
            </p>
          )}
          <label className="mt-5 block text-sm font-bold">
            Email
            <input
              required
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 min-h-12 w-full rounded-xl border border-slate-300 px-3"
            />
          </label>
          <label className="mt-4 block text-sm font-bold">
            Password
            <input
              required
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 min-h-12 w-full rounded-xl border border-slate-300 px-3"
            />
          </label>
          <button
            disabled={busy}
            className="mt-6 min-h-14 w-full rounded-xl bg-[#00549C] px-4 font-black text-white disabled:opacity-50"
          >
            {busy ? 'SIGNING IN…' : 'SIGN IN TO POS'}
          </button>
          <Link to="/owner" className="mt-4 block text-center text-sm font-semibold text-[#00549C]">
            Owner dashboard
          </Link>
        </form>
      </div>
      {recoveryOpen && <LocalDataRecoveryDialog onClose={closeRecovery} />}
    </div>
  );
}
